/**
 * Attendance Component Spec
 *
 * Purpose:
 * Provides the main attendance tracking interface for students and administrators.
 * Displays real-time attendance status, allows sign in/out, and manages attendance records.
 *
 * Core Requirements:
 * 1. Display student attendance status with sign in/out times
 * 2. Show hours earned and daily requirements with color coding
 * 3. Display personal days used (unexcused absences) out of allowed total
 * 4. Support admin-only features for editing historical attendance
 * 5. Group students by 5 attendance categories on current day
 * 6. Show warning/violation indicators with color coding (yellow/red/green)
 * 7. Support granting hours for absences and partial attendance
 * 8. Auto-refresh current day attendance every 6 minutes for non-admin users
 * 9. Play different sounds for success vs failure events
 * 10. Support mock mode for development/testing
 * 11. Report violations for missing sign in/out or not notifying when absent
 * 12. Highlight incomplete attendance records from past days
 * 13. Auto-fill current time for non-admin sign in/out
 * 14. Integrate with off-campus system and validate workflows
 * 15. Check for holidays, weekends, and school year boundaries
 *
 * Student Categories (Current Day Only):
 * - "Not Here Yet": No attendance record exists
 * - "Here": Signed in but not signed out
 * - "Off Campus": Currently signed out for off-campus activity
 * - "Signed Out": Signed in and signed out for the day
 * - "Absent": Has attendance record but no sign in time (personal day or granted absence)
 *
 * Data Contract with Backend:
 * - Receives student data from /api/students/with-attendance-data endpoint
 * - Key fields from API:
 *   - unexcused_absences: Number of personal days used (days with day_earned = false)
 *   - unexcused_absences_allowed: Maximum personal days allowed (from school_calendar)
 *   - daily_hours_required: Hours needed per day to meet annual requirement
 *   - total_hours_attended: Sum of all hours_earned for the school year
 *   - warning_status: '', 'warning', or 'violation'
 *   - attendance: Current day's attendance record (if exists)
 *   - default_granted_hours: Hours to grant for excused absences
 *   - violations: Array of unpaid violations for the student
 *   - no_attendance_requirement: Flag for exempt students
 *
 * Admin Features:
 * - Edit any attendance record for any date
 * - Toggle between absent/present status
 * - Toggle between personal day/granted absence for absent students
 * - Grant hours for present students (partial day credit)
 * - Report violations (3 types: didn't sign in, didn't notify, didn't sign out)
 * - Delete attendance records
 * - Navigate to any date including weekends/holidays
 *
 * Non-Admin (Tablet) Features:
 * - One-click sign in/out with current time auto-filled
 * - Warning messages for attendance violations
 * - Fine notifications for unpaid violations
 * - Auto-refresh every 6 minutes to keep data current
 * - Sound feedback (success for sign in/out, failure for errors)
 * - Persistent warning after 5 seconds of modal open
 * - Prevented from signing out if off-campus (must use off-campus page)
 *
 * Color Coding:
 * - Green background: Hours earned meet daily requirement or no warning status
 * - Black background: Hours earned below daily requirement
 * - Yellow background: Warning status (approaching violation threshold)
 * - Red background: Violation status (exceeded threshold)
 * - Red highlight: Incomplete attendance records (missing sign out from past days)
 *
 * Grant Hours Logic:
 * - Absent students: Can be marked as personal day (no granted hours) or granted absence
 * - Granted absence: Uses default_granted_hours from API, requires reason selection
 * - Present students: Can grant additional hours for partial attendance (appointments, etc.)
 * - Reason options: Sick, Trip, Work Study, Hardship, Field Trip, Medical Appointment,
 *                   Weather, Religious Holiday, Suspended, Other
 *
 * Violation Reporting:
 * - Admin can report 3 types of violations:
 *   - Law Code 500.02.02: "Didn't Notify the School when Absent"
 *   - Law Code 500.02.01: "Didn't Sign In"
 *   - Law Code 500.02.01: "Didn't Sign Out"
 * - Violations create fines that are displayed to students
 * - Total fines are calculated and shown when signing in/out
 *
 * UI Sections:
 * - Header: Date display with admin date picker
 * - Student Table: 6-7 columns (Name, In, Out, Absent?, [Granted Hours], Hours, Personal)
 * - Admin Modal: Complex form for editing attendance with validation
 * - User Modal: Simple sign in/out button with warnings
 * - Violation Dialog: Admin tool for reporting violations
 * - Delete Confirmation: Safety dialog for record deletion
 * - Snackbar Alerts: Temporary and persistent notifications
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Table, TableBody, TableCell, TableContainer, TableRow, Paper,
    Modal, Box, TextField, Button, MenuItem, FormControl, IconButton,
    InputLabel, Select, Typography, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TableHead, FormHelperText,
    Switch, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import WarningIcon from '@mui/icons-material/Warning';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { format } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { CircularProgress } from '@mui/material';
import { formatTime24To12, formatDate, authenticatedFetch as actualAuthenticatedFetch, roundToNearestTenth, isWeekend, getAbsenceDisplay, getSchoolStatus } from '../utils/utils';
import { Howl } from 'howler';
import { differenceInMinutes, isBefore, startOfDay, parse, setZone, setMilliseconds, setSeconds, setMinutes, setHours } from 'date-fns';
import { isMockMode, mockAuthenticatedFetch } from '../utils/mockApi';
import AttendanceRecordModal from '../components/AttendanceRecordModal';

// Re-export for testing
export { isMockMode, mockAuthenticatedFetch };

const Attendance = ({ isAdmin }) => {
    const stickyHeaderStyle = {
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 1,
    };

    const params = useParams();
    const now = new Date();

    const getInitialDate = () => {
        if (params.date) {
            try {
                const dateOnly = params.date.split('T')[0];
                const parsedDate = parse(dateOnly, 'yyyy-MM-dd', new Date());
                if (!isNaN(parsedDate.getTime())) {
                    return parsedDate;
                }
            } catch (error) {
                console.error('Error parsing initial date:', error);
            }
        }
        return now;
    };

    const [reloadTime, setReloadTime] = useState('');
    const [students, setStudents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(getInitialDate());
    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        student: null,
        date: null,
        signed_in_at: '',
        signed_out_at: '',
        // UI state fields
        is_absent: false,
        is_personal_day: true,
        grant_hours: false,
        granted_hours: 0,
        granted_reason: '',
        granted_details: '',
        // Calculated fields (from API)
        hours_earned: 0,
        day_earned: false
    });
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [persistentAlert, setPersistentAlert] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState(null);
    const [showViolationDialog, setShowViolationDialog] = useState(false);
    const [violationType, setViolationType] = useState('');
    const [calendarInfo, setCalendarInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const currentDayIsSelected = selectedDate.toDateString() === now.toDateString();
    
    // Initialize mock mode if enabled - do this immediately
    // Check URL params from React Router
    const location = window.location;
    const urlParams = new URLSearchParams(location.search);
    const mockModeEnabled = urlParams.has('mockAPI');
    
    // Use mock fetch function when mock mode is enabled
    const authenticatedFetch = mockModeEnabled ? mockAuthenticatedFetch : actualAuthenticatedFetch;

    const successSound = new Howl({
        src: ['/success.mp3']
    });

    const failureSound = new Howl({
        src: ['/failure.mp3']
    });

    const playSound = (sound) => {
        if (!isAdmin) {  // Only play sounds for non-admin users
            sound.play();
        }
    };

    const clearField = (field) => {
        setFormData({ ...formData, [field]: '' });
    };

    const fetchStudents = useCallback(async () => {
        const response = await authenticatedFetch(`/students/with-attendance-data/${selectedDate.toISOString()}`);
        if (!response.ok) throw new Error('Failed to fetch students');

        setStudents(await response.json());
        setLastFetchTime(new Date());
    }, [selectedDate, authenticatedFetch]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setIsLoading(true);

            try {
                await fetchStudents();
                const calendarResponse = await authenticatedFetch(`/calendar/for-day/${selectedDate.toISOString().split('T')[0]}`);
                if (calendarResponse.ok) {
                    setCalendarInfo(await calendarResponse.json());
                } else {
                    throw new Error('Failed to fetch calendar information');
                }
            } catch (err) {
                setAlert({ message: err.message });
            } finally {
                setLoading(false);
                setIsLoading(false);
            }
        }
        fetchData();
    }, [selectedDate, fetchStudents]);

    useEffect(() => {
        if (!isAdmin) {
            const msUntilTomorrow = () => {
                const now = new Date();
                const tomorrow = new Date(now);
                tomorrow.setHours(25, 0, 0, 0);
                setReloadTime(tomorrow.toISOString());
                return tomorrow.getTime() - now.getTime();
            };

            const updateToDate = () => {
                window.location.reload();
            };

            const timeout = setTimeout(updateToDate, msUntilTomorrow());

            return () => {
                setReloadTime('');
                clearTimeout(timeout);
            }
        }
    }, [isAdmin]);

    useEffect(() => {
        // Only set up the interval if the current day is selected, the user is not an admin, and the modal is not open
        if (currentDayIsSelected && !isAdmin && !modalOpen) {
            const intervalId = setInterval(() => {
                //update the selected date to the current time and refresh the attendance records
                setSelectedDate(new Date());
            }, 1000 * 60 * 6); // Update every 6 minutes
    
            // Cleanup the interval on component unmount or when conditions change
            return () => clearInterval(intervalId);
        }
    }, [isAdmin, modalOpen, currentDayIsSelected]);

    const schoolStatus = () => getSchoolStatus(selectedDate, calendarInfo);

    const categorizedStudents = () => {
        const categories = {
            "Not Here Yet": [],
            "Here": [],
            "Off Campus": [],
            "Signed Out": [],
            "Absent": []
        };

        students.forEach(student => {
            if (student.is_off_campus) {
                categories["Off Campus"].push(student);
            } else if (student.attendance) {
                // Check if student is present (has sign in time) or absent
                if (student.attendance.signed_in_at) {
                    // Student signed in (present)
                    if (student.attendance.signed_out_at) {
                        categories["Signed Out"].push(student);
                    } else {
                        categories["Here"].push(student);
                    }
                } else {
                    // No sign in time means absent (personal day or granted absence)
                    categories["Absent"].push(student);
                }
            } else {
                // No attendance record at all
                categories["Not Here Yet"].push(student);
            }
        });

        return categories;
    };

    const currentTime = () => {
        return new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
    }

    const handleCellClick = (student) => {
        const attendance = student.attendance;

        // Determine UI state based on existing attendance data
        let isAbsent = false; // Default to present if no record
        let isPersonalDay = false; // Default to granted absence (not personal day)
        let grantHours = false;
        let signedIn = '';
        let signedOut = '';
        let grantedHours = student.default_granted_hours || 0;
        let grantedReason = '';
        let grantedDetails = '';
        let hoursEarned = 0;
        let dayEarned = false;

        if (attendance) {
            // Determine if absent based on sign in/out times
            isAbsent = !attendance.signed_in_at && !attendance.signed_out_at;
            
            if (isAbsent) {
                // For absent students, check if personal day or granted absence
                // grant_hours controls whether granted fields are shown
                grantHours = Boolean(attendance.granted_hours && attendance.granted_hours > 0);
                if (grantHours) {
                    grantedHours = attendance.granted_hours || student.default_granted_hours || 0;
                    grantedReason = attendance.granted_reason || '';
                    grantedDetails = attendance.granted_details || '';
                }
                // Set is_personal_day for backend compatibility
                isPersonalDay = !grantHours;
            } else {
                // For present students
                signedIn = attendance.signed_in_at || '';
                signedOut = attendance.signed_out_at || '';
                if (!isAdmin && signedIn && !signedOut) {
                    signedOut = currentTime(); // Auto-fill sign out for non-admin
                }
                grantHours = Boolean(attendance.granted_hours && attendance.granted_hours > 0);
                if (grantHours) {
                    grantedHours = attendance.granted_hours || 0;
                    grantedReason = attendance.granted_reason || '';
                    grantedDetails = attendance.granted_details || '';
                }
            }
            
            hoursEarned = attendance.hours_earned || 0;
            dayEarned = attendance.day_earned || false;
        } else if (!isAdmin) {
            // For non-admin users creating new record, default to present
            isAbsent = false;
            signedIn = currentTime();
        }

        const defaultFormData = {
            student: student,
            date: formatDate(selectedDate),
            signed_in_at: signedIn,
            signed_out_at: signedOut,
            // UI state fields
            is_absent: isAbsent,
            is_personal_day: isPersonalDay,
            grant_hours: grantHours,
            granted_hours: grantedHours,
            granted_reason: grantedReason,
            granted_details: grantedDetails,
            hours_earned: hoursEarned,
            day_earned: dayEarned,
            violations: student.violations,
            // Store the original attendance ID if editing
            id: attendance?.id
        }

        setFormData(defaultFormData);
        setFormErrors({});
        setModalOpen(true);
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // New toggle handlers for the switches
    const handleAbsentToggle = () => {
        const newIsAbsent = !formData.is_absent;
        
        if (newIsAbsent) {
            // Switching to absent - clear sign in/out times, grant_hours defaults to false (personal day)
            setFormData({
                ...formData,
                is_absent: true,
                is_personal_day: false, // Keep for backend compatibility
                grant_hours: false, // Defaults to personal day
                signed_in_at: '',
                signed_out_at: '',
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Switching to present - set default sign in time, clear granted fields
            setFormData({
                ...formData,
                is_absent: false,
                is_personal_day: false,
                grant_hours: false,
                signed_in_at: isAdmin ? '' : currentTime(),
                signed_out_at: '',
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    const handlePersonalDayToggle = () => {
        const newIsPersonalDay = !formData.is_personal_day;
        
        if (newIsPersonalDay) {
            // Switching to personal day - clear granted fields
            setFormData({
                ...formData,
                is_personal_day: true,
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Switching to granted absence - set default granted hours
            setFormData({
                ...formData,
                is_personal_day: false,
                granted_hours: formData.student?.default_granted_hours || 4.0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    const handleGrantHoursToggle = () => {
        const newGrantHours = !formData.grant_hours;
        
        if (newGrantHours) {
            // Enable grant hours - set default values
            setFormData({
                ...formData,
                grant_hours: true,
                granted_hours: formData.student?.default_granted_hours || 4.0,
                granted_reason: '',
                granted_details: ''
            });
        } else {
            // Disable grant hours - clear granted fields
            setFormData({
                ...formData,
                grant_hours: false,
                granted_hours: 0,
                granted_reason: '',
                granted_details: ''
            });
        }
    };

    const validateFormData = (data = formData) => {
        const errors = {};

        // New validation logic based on UI state
        if (!data.is_absent) {
            // Present student validation
            if (!data.signed_in_at) {
                errors.signed_in_at = "Sign in time is required for present students";
            }
            if (data.signed_in_at && data.signed_out_at && data.signed_in_at > data.signed_out_at) {
                errors.signed_out_at = "Sign-out time must be after sign-in time";
            }
            if (!isAdmin && data.signed_out_at && data.signed_in_at && data.student.is_off_campus) {
                errors.general = "Go to the Off-Campus page to sign in before you sign out for the day";
            }

            // Grant hours validation for present students
            if (data.grant_hours) {
                if (!data.granted_hours || data.granted_hours <= 0) {
                    errors.granted_hours = "Granted hours must be greater than 0";
                }
                if (!data.granted_reason) {
                    errors.granted_reason = "Reason is required when granting hours";
                }
            }
        } else {
            // Absent student validation
            if (data.grant_hours) {
                // Granted absence validation (when grant_hours is enabled)
                if (!data.granted_hours || data.granted_hours <= 0) {
                    errors.granted_hours = "Granted hours is required";
                }
                if (!data.granted_reason) {
                    errors.granted_reason = "Reason is required when granting hours";
                }
            }
        }

        // General validation
        if (data.granted_hours && (data.granted_hours < 0 || data.granted_hours > 24)) {
            errors.granted_hours = "Granted hours must be between 0 and 24";
        }

        return errors;
    };

    // Backward compatibility wrapper
    const validateForm = () => validateFormData(formData);

    const saveAttendance = async (e) => {
        // Handle both event (from form submit) and formData (from AttendanceRecordModal)
        let dataToValidate = formData;

        if (e && e.preventDefault) {
            e.preventDefault();
        } else if (e && typeof e === 'object' && e.is_absent !== undefined) {
            // e is actually formData from AttendanceRecordModal
            dataToValidate = e;
            setFormData(e);
        }

        const errors = validateFormData(dataToValidate);
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            playSound(failureSound);
            return;
        }

        setLoading(true);

        // Prepare data to send based on UI state
        const dataToSend = {
            ...dataToValidate,
            // Set sign in/out times based on absent state
            signed_in_at: dataToValidate.is_absent ? null : (dataToValidate.signed_in_at || null),
            signed_out_at: dataToValidate.is_absent ? null : (dataToValidate.signed_out_at || null),
            // Set granted fields based on grant_hours state
            granted_hours: dataToValidate.grant_hours ? dataToValidate.granted_hours : 0,
            granted_reason: dataToValidate.grant_hours ? dataToValidate.granted_reason : null,
            granted_details: dataToValidate.grant_hours ? dataToValidate.granted_details : null
        };

        try {
            const response = await authenticatedFetch(`/attendance/record/${dataToValidate.student.id}/${dataToValidate.date}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                setModalOpen(false);
                setPersistentAlert(null);
                setSelectedDate(currentDayIsSelected ? new Date() : new Date(selectedDate));
                let message = isAdmin ? 'Attendance record saved successfully' : (formData.signed_out_at ? 'Signed out successfully' : 'Signed in successfully');
                setAlert({
                    message: message,
                    severity: 'success',
                    autoHideDuration: 3000
                });
                playSound(successSound);
            } else {
                throw new Error('Failed to save attendance record');
            }
        } catch (error) {
            setAlert({ message: 'Failed to save attendance record', severity: 'error', autoHideDuration: 3000 });
            console.error('Error saving attendance record:', error);
            playSound(failureSound);
        } finally {
            setLoading(false);
        }
    };

    const deleteAttendance = async () => {
        setLoading(true);
        try {
            const response = await authenticatedFetch(`/attendance/record/${formData.student.id}/${formData.date}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setSelectedDate(currentDayIsSelected ? new Date() : new Date(selectedDate));// Refresh attendance records
                setModalOpen(false);
            } else {
                throw new Error('Failed to delete attendance record');
            }
        } catch (error) {
            setAlert({ message: 'Failed to delete attendance record' });
            console.error('Error deleting attendance record:', error);
        } finally {
            setLoading(false);
            setShowDeleteConfirm(false);
        }
    };

    // Modal style
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 2,
    };

    const handleSelectedDateChange = (date) => {
        setOpenDatePicker(false);
        setSelectedDate(date);
    };

    const renderTableBody = () => {
        if (currentDayIsSelected) {
            return renderGroupedRows();
        } else {
            return renderStandardRows(students);
        }
    };


    const renderGroupedRows = () => (
        Object.entries(categorizedStudents()).map(([category, students]) => (
            <React.Fragment key={category}>
                <TableRow key={category}>
                    <TableCell colSpan={isAdmin ? 7 : 5} style={{ backgroundColor: '#f0f0f0' }}>{category} ({students.length})</TableCell>
                </TableRow>
                {renderStandardRows(students)}
            </React.Fragment>
        ))
    )

    const renderPersonalDaysUsed = (student) => {
        if (student.no_attendance_requirement) {
            return <span style={{ color: '#9e9e9e' }}>N/A</span>;
        }
        if (currentDayIsSelected || isAdmin) {
            // Check if data is missing
            if (student.unexcused_absences === null || student.unexcused_absences === undefined ||
                student.unexcused_absences_allowed === null || student.unexcused_absences_allowed === undefined) {
                return <span className="error-icon" style={{ color: 'red' }}>!</span>;
            }

            // Use unexcused_absences and unexcused_absences_allowed from API
            const used = student.unexcused_absences;
            const allowed = student.unexcused_absences_allowed;
            return (
                <span style={{ color: used >= allowed ? 'red' : 'black' }}>
                    {used} / {allowed}
                </span>
            )
        }
        else {
            return '—';
        }
    }

    const shouldHighlightInvalidRecord = (date) => {
        const now = new Date();
        const today = startOfDay(now);
        const dateToCheck = startOfDay(date);
        
        // Past weekdays should always be highlighted
        if (isBefore(dateToCheck, today) && !isWeekend(dateToCheck)) {
            return true;
        }
        
        // Current day after 4pm should be highlighted
        if (dateToCheck.getTime() === today.getTime() && now.getHours() >= 16) {
            return true;
        }
        
        return false;
    };

    const isIncompleteAttendance = (attendance) => {
        // No attendance record at all
        if (!attendance) {
            return true;
        }

        // Personal day or granted absence: no sign-in and no sign-out
        // These are complete records (whether granted_hours is 0 or >0)
        if (!attendance.signed_in_at && !attendance.signed_out_at) {
            return false; // Complete - it's either a personal day or granted absence
        }

        // Missing sign-out (signed in but didn't sign out)
        if (attendance.signed_in_at && !attendance.signed_out_at) {
            return true; // Incomplete
        }

        // All other cases are complete (e.g., signed in and signed out)
        return false;
    };
    

    const renderStandardRows = (students) => (
        students.map(student => (
            <TableRow
                key={student.id}
                sx={{
                    cursor: 'pointer',
                    backgroundColor: (shouldHighlightInvalidRecord(selectedDate) && isIncompleteAttendance(student.attendance)) ? '#FFCCCB' : 'inherit'
                }}
                onClick={() => handleCellClick(student)}
            >
                <TableCell color="primary">
                    <Typography color="primary">
                        {student.nickname}
                    </Typography>
                </TableCell>
                <TableCell>
                    {student.attendance && student.attendance.signed_in_at ? formatTime24To12(student.attendance.signed_in_at) : '—'}
                </TableCell>
                <TableCell>
                    {student.attendance && student.attendance.signed_out_at ? formatTime24To12(student.attendance.signed_out_at) : '—'}
                </TableCell>
                {isAdmin && (
                    <TableCell>
                        {(() => {
                            const display = getAbsenceDisplay(student.attendance, { showEarnedDay: false });
                            if (display === "!") {
                                return <span className="error-icon" style={{ color: 'red' }}>!</span>;
                            }
                            return display;
                        })()}
                    </TableCell>
                )}
                {isAdmin && (
                    <TableCell>
                        {student.attendance ? (student.attendance.granted_hours || '—') : '—'}
                    </TableCell>
                )}
                <TableCell>
                {student.no_attendance_requirement ? (
                    <span style={{ color: '#9e9e9e' }}>N/A</span>
                ) : (
                    <>
                        {student.attendance && (student.attendance.hours_earned === null || student.attendance.hours_earned === undefined) ? (
                            <span className="error-icon" style={{ color: 'red' }}>!</span>
                        ) : (
                            <>
                                <span style={{
                                    color: 'white',
                                    backgroundColor: (student.attendance && student.attendance.hours_earned >= student.daily_hours_required) 
                                                    ? '#32CD32' // Green background if hours earned meet or exceed daily requirement
                                                    : 'black',  // Black background if hours earned do not meet the daily requirement
                                    padding: '2px 4px',
                                    borderRadius: '4px',
                                }}>
                                    {roundToNearestTenth((student.attendance && student.attendance.hours_earned) || 0)}
                                </span>
                                &nbsp;of&nbsp;
                                <span style={{
                                    backgroundColor: student.warning_status === 'warning' ? '#FFD700' 
                                                    : student.warning_status === 'violation' ? 'red' 
                                                    : student.warning_status === '' ? '#32CD32' // LimeGreen for appropriate green background
                                                    : 'transparent',
                                    color: 'white',
                                    padding: '2px 4px',
                                    borderRadius: '4px'
                                }}>
                                    {roundToNearestTenth(student.daily_hours_required)}
                                </span>
                            </>
                        )}
                    </>
                )}
                </TableCell>
                <TableCell onClick={(e) => {
                    if (isAdmin) {
                        e.stopPropagation();
                        if (student.unexcused_absences) setSelectedDate(new Date(student.unexcused_absences));
                    }
                }}>
                    {renderPersonalDaysUsed(student)}
                </TableCell>
            </TableRow>
        ))
    )

    useEffect(() => {
        let timeoutId;
        let intervalId;

        if (modalOpen && !isAdmin) {
            timeoutId = setTimeout(() => {
                setPersistentAlert({
                    message: 'You forgot to sign in or out!',
                    severity: 'warning'
                });

                // Only start playing the sound if there are no warning messages
                if (!formData.student.warning_status && (!formData.student.violations || formData.student.violations.length === 0)) {
                    intervalId = setInterval(() => {
                        playSound(failureSound);
                    }, 1000);
                }

            }, 5000); // 5 seconds timeout
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (intervalId) clearInterval(intervalId);
            setPersistentAlert(null);
        };
    }, [modalOpen, isAdmin, formData.student]);

    // Modify the setModalOpen function to clear the interval when the modal is closed
    const closeModal = () => {
        setModalOpen(false);
        setPersistentAlert(null); // Clear the persistent alert when closing the modal
    };

    const updateHoursEarned = useCallback(() => {
        if (!lastFetchTime || !currentDayIsSelected) return;

        const now = new Date();
        if (now.getHours() >= 16) return; // Stop updating at 4 PM

        setStudents(prevStudents => 
            prevStudents.map(student => {
                if (student.attendance && student.attendance.signed_in_at && !student.attendance.signed_out_at) {
                    const minutesSinceLastFetch = differenceInMinutes(now, lastFetchTime);
                    const additionalHours = minutesSinceLastFetch / 60;
                    return {
                        ...student,
                        attendance: {
                            ...student.attendance,
                            hours_earned: roundToNearestTenth(student.attendance.hours_earned + additionalHours)
                        }
                    };
                }
                return student;
            })
        );

        setLastFetchTime(now);
    }, [lastFetchTime, currentDayIsSelected]);

    useEffect(() => {
        let intervalId;
        if (currentDayIsSelected && !isAdmin) {
            intervalId = setInterval(() => {
                updateHoursEarned();
            }, 60000); // Update every minute
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [currentDayIsSelected, isAdmin, updateHoursEarned]);

    const reportViolation = async (violationType) => {
        if (!formData.student) return;
    
        let violationData;
        if (violationType === "didntSignIn") {
            violationData = {
                student_id: formData.student.id,
                law_code: "500.02.01",
                description: "Didn't Sign In"
            };
        } else if (violationType === "didntNotify") {
            violationData = {
                student_id: formData.student.id,
                law_code: "500.02.02",
                description: "Didn't Notify the School when Absent"
            };
        } else if (violationType === "didntSignOut") {
            violationData = {
                student_id: formData.student.id,
                law_code: "500.02.01",
                description: "Didn't Sign Out"
            };
        } else {
            return;
        }

        // A violation reported while viewing a past day occurred on that day. Noon keeps
        // the date the same in Eastern time and UTC. Today's violations use the server's now.
        if (!currentDayIsSelected) {
            const occurredAt = new Date(selectedDate);
            occurredAt.setHours(12, 0, 0, 0);
            violationData.timestamp = occurredAt.toISOString();
        }

        try {
            const response = await authenticatedFetch('/violations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(violationData)
            });
    
            if (response.ok) {
                setAlert({
                    message: 'Violation reported successfully',
                    severity: 'success',
                    autoHideDuration: 3000
                });
                playSound(successSound);
            } else {
                throw new Error('Failed to report violation');
            }
        } catch (error) {
            setAlert({
                message: 'Failed to report violation',
                severity: 'error',
                autoHideDuration: 3000
            });
            console.error('Error reporting violation:', error);
            playSound(failureSound);
        } finally {
            setShowViolationDialog(false);
            setViolationType('');
        }
    };
    
    const handleReportViolation = () => {
        setShowViolationDialog(true);
    };

    const calculateTotalFines = (violations) => {
        return violations ? violations.length : 0;
    };
    
    return (
        <>
            {mockModeEnabled && (
                <Box sx={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '8px',
                    textAlign: 'center',
                    borderBottom: '1px solid #ffeaa7'
                }}>
                    Mock API Mode - Test Data Only
                </Box>
            )}
            {loading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 'tooltip',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <CircularProgress />
                </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'bottom' }}>
                <Typography variant="h5" gutterBottom>
                    {format(selectedDate, 'eeee, MMMM do, yyyy')}
                </Typography>
                {isAdmin && (<Button sx={{ mb: 2 }} onClick={() => setOpenDatePicker(true)}>Change Day</Button>)}
                {openDatePicker && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            open={openDatePicker}
                            value={selectedDate}
                            onChange={handleSelectedDateChange}
                            onClose={() => setOpenDatePicker(false)}
                            views={['day']}
                            shouldDisableDate={(date) => [0, 6].includes(date.getDay())}
                        />
                    </LocalizationProvider>
                )}
            </Box>
            {isLoading ? (
                <>
                </>
            ) : schoolStatus().isSchoolDay ? (
                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 150px)' }}>
                    <Table sx={{ tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0 }} aria-label="attendance table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={stickyHeaderStyle}>Name</TableCell>
                                <TableCell sx={stickyHeaderStyle}>In</TableCell>
                                <TableCell sx={stickyHeaderStyle}>Out</TableCell>
                                {isAdmin && (
                                    <TableCell sx={stickyHeaderStyle}>Absent?</TableCell>
                                )}
                                {isAdmin && (
                                    <TableCell sx={stickyHeaderStyle}>Granted Hours</TableCell>
                                )}
                                <TableCell sx={stickyHeaderStyle}>Hours</TableCell>
                                <TableCell sx={stickyHeaderStyle}>Personal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderTableBody()}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="h4" align="center" sx={{ mt: 4 }}>
                    {schoolStatus().message}
                </Typography>
            )}

            {formData.student && isAdmin && (
                <AttendanceRecordModal
                    open={modalOpen}
                    title={
                        <Link
                            to={`/student-attendance/${formData.student.id}`}
                            style={{ textDecoration: 'none' }}
                        >
                            <Typography color="primary">
                                {formData.student.nickname}
                            </Typography>
                        </Link>
                    }
                    showDateField={false}
                    showDeleteButton={!!formData.id}
                    initialFormData={formData}
                    errors={formErrors}
                    onSave={saveAttendance}
                    onDelete={() => setShowDeleteConfirm(true)}
                    onClose={closeModal}
                    onReportViolation={handleReportViolation}
                    onClearField={clearField}
                />
            )}

            {formData.student && !isAdmin && (
                <Modal
                    open={modalOpen}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            closeModal();
                        }
                    }}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={modalStyle}>
                        <Box sx={{ position: 'relative' }}>
                                    <IconButton
                                        onClick={closeModal}
                                        sx={{ position: 'absolute', right: 0, top: 0 }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <Typography variant="h6" sx={{ textAlign: "center", paddingTop: '20px' }}>
                                        {formData.student.nickname}
                                    </Typography>
                                    {formData.student.warning_status && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            backgroundColor: formData.student.warning_status === 'warning' ? '#FFD700' : 'red', 
                                            color: formData.student.warning_status === 'warning' ? '#000000' : '#FFFFFF', // Change text color
                                            padding: '12px', 
                                            borderRadius: '4px', 
                                            mb: 2, 
                                            mt: 2, 
                                            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)' 
                                        }}>
                                            {formData.student.warning_status === 'warning' && <WarningIcon sx={{ mr: 1 }} />}
                                            {formData.student.warning_status === 'violation' && <ReportProblemIcon sx={{ mr: 1 }} />}
                                            <Typography variant="body1" sx={{ textAlign: 'center' }}>
                                                {formData.student.warning_status === 'warning'
                                                    ? `You're behind on attendance and must now attend ${Math.floor(formData.student.daily_hours_required)} hours and ${Math.round((formData.student.daily_hours_required % 1) * 60)} minutes per day.`
                                                    : `You're in violation of attendance law 500.01.03 and must now attend ${Math.floor(formData.student.daily_hours_required)} hours and ${Math.round((formData.student.daily_hours_required % 1) * 60)} minutes per day.`}
                                            </Typography>
                                        </Box>
                                    )}
                                    {formData.student.violations && formData.student.violations.length > 0 && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            backgroundColor: '#FFD700', // Keep the yellow background
                                            color: '#000000', // Change text color to black for better contrast
                                            padding: '12px', 
                                            borderRadius: '4px', 
                                            mb: 2, 
                                            mt: 2, 
                                            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)' 
                                        }}>
                                            <ReportProblemIcon sx={{ mr: 1, color: '#000000' }} />
                                            <Typography variant="body1" sx={{ textAlign: 'center' }}>
                                                You have ${calculateTotalFines(formData.student.violations)} attendance fines. See an Attendance Clerk for more information.
                                            </Typography>
                                        </Box>
                                    )}
                                    {formErrors.general && (
                                        <Typography variant="h6" sx={{ textAlign: "center" }} color="red">
                                            {formErrors.general}
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: 4
                                    }}
                                >
                                    {(!formData.student.attendance || !formData.student.attendance.signed_out_at) && (
                                        <div
                                            style={{
                                                position: 'relative',
                                                display: 'inline-block',
                                                width: '140px',  // Matching the overall clickable area width from the off-campus modal
                                                height: '80px',  // Matching the overall clickable area height from the off-campus modal
                                                cursor: 'pointer' // Ensure it shows as clickable
                                            }}
                                            onClick={saveAttendance} // Bind the click event to the whole container
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                style={{
                                                    position: 'absolute',
                                                    top: '20px',  // Offset by half the increased height to center the button visually
                                                    left: '20px', // Offset by half the increased width to center the button visually
                                                    width: '100px',  // The visible width of the button
                                                    height: '40px',  // The visible height of the button
                                                    padding: '0',    // Keep the padding default
                                                    zIndex: 2,       // Ensure the button is above the clickable area
                                                }}
                                            >
                                                {formData.student.attendance && formData.student.attendance.signed_in_at && !formData.student.attendance.signed_out_at ? 'Sign Out' : 'Sign In'}

                                            </Button>
                                        </div>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        position: 'absolute',
                                        bottom: 8,
                                        right: 8,
                                    }}
                                >
                                </Box>
                    </Box>
                </Modal>
            )}

            <Dialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this attendance record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={deleteAttendance} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={showViolationDialog}
                onClose={() => setShowViolationDialog(false)}
                aria-labelledby="violation-dialog-title"
                aria-describedby="violation-dialog-description"
            >
                <DialogTitle id="violation-dialog-title">{"Report Violation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="violation-dialog-description">
                        Please select the type of violation:
                    </DialogContentText>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <Button 
                            onClick={() => reportViolation("didntSignIn")} 
                            style={{ color: '#f44336' }}
                            variant="text"
                        >
                            Didn't Sign In
                        </Button>
                        <Button 
                            onClick={() => reportViolation("didntNotify")} 
                            style={{ color: '#f44336' }}
                            variant="text"
                        >
                            Didn't Notify the School
                        </Button>
                        <Button 
                            onClick={() => reportViolation("didntSignOut")} 
                            style={{ color: '#f44336' }}
                            variant="text"
                        >
                            Didn't Sign Out
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowViolationDialog(false)} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {persistentAlert && (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={!!persistentAlert}
                >
                    <Alert severity={persistentAlert.severity} sx={{ width: '100%' }}>
                        {persistentAlert.message}
                    </Alert>
                </Snackbar>
            )}

            {alert && (
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={!!alert}
                    autoHideDuration={alert.autoHideDuration || 6000}
                    onClose={() => setAlert(null)}
                >
                    <Alert onClose={() => setAlert(null)} severity={alert.severity || 'error'} sx={{ width: '100%' }}>
                        {alert.message}
                    </Alert>
                </Snackbar>
            )}

            <Typography sx={{ mt: 1, fontSize: 10 }} color="gray">
                Will reload at {reloadTime}
            </Typography>
        </>
    );
};

export default Attendance;